/**
 * PackingProductsModal - Modal de productos siendo empacados
 *
 * Modal que muestra solo los productos que están actualmente siendo
 * empacados (processedQuantity > 0). Incluye:
 * - Lista filtrada de productos en packing
 * - Estadísticas de productos empacados vs total esperado
 * - Progreso individual por producto
 * - Integración con localStorage para cantidades asignadas
 * - Mensaje cuando no hay productos en packing
 */

import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingBag } from '@fortawesome/pro-light-svg-icons';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { usePackingContext } from '../../hooks';

export const PackingProductsModal = forwardRef<ModalCenterRef, { assignedQuantities?: Record<string, number> }>(
  (props, ref) => {
    const { t } = useTranslation('packingModule');
    const { products, data, orders } = usePackingContext() as any;
    const { assignedQuantities: assignedFromProps } = props;

    // Obtener cantidades asignadas actuales desde localStorage (mismo patrón que la pantalla)
    const getAssignedQuantities = (): Record<string, number> => {
      if (assignedFromProps && Object.keys(assignedFromProps).length > 0) {
        return assignedFromProps;
      }
      try {
        const routeKey = `packing:assigned:${data?.preparation?.id || 'unknown'}`;
        return JSON.parse(localStorage.getItem(routeKey) || '{}');
      } catch {
        return {};
      }
    };

    // Logs eliminados

    // Calcular processedQuantity igual que en ProductForm.tsx
    // Nota: usamos órdenes con status_id = 6 (terminadas) para computar procesados
    const productsWithRealProcessed = products.map(
      (product: {
        id: string;
        name: string;
        code: string;
        image: string;
        expectedQuantity: number;
        processedQuantity: number;
      }) => {
        const isPreparationComplete = data?.preparation?.status === 3;

        // Cantidad completada proveniente de pedidos terminados (status_id = 6)
        const completedQuantityFromFinishedOrders =
          orders?.reduce((total: number, order: any) => {
            if (order.status_id !== 6) return total;

            let orderCompletedQty = 0;
            (order.lines || []).forEach((line: any) => {
              if (line.pack && line.pack.lines) {
                // Es un pack - verificar cada producto del pack
                line.pack.lines.forEach((packLine: any) => {
                  if (packLine.product?.code === product.code) {
                    orderCompletedQty += packLine.qty || 0;
                  }
                });
              } else if (line.product && line.product.code === product.code) {
                // Es un producto individual
                orderCompletedQty += line.qty || 0;
              }
            });

            return total + orderCompletedQty;
          }, 0) || 0;

        // Cantidad asignada actualmente (sumatoria por código de producto en todas las órdenes)
        const assignedQuantitiesMap = getAssignedQuantities();
        const assignedForProduct = Object.entries(assignedQuantitiesMap)
          .filter(([key]) => key.split(':')[1] === product.code)
          .reduce((sum, [, qty]) => sum + Number(qty), 0);

        // Procesado = completado (órdenes terminadas) + asignado en curso
        const processedBase = isPreparationComplete
          ? product.expectedQuantity
          : completedQuantityFromFinishedOrders + assignedForProduct;

        const processed = Math.min(processedBase, product.expectedQuantity);

        return {
          ...product,
          processedQuantity: processed,
          isCompleted: processed >= product.expectedQuantity,
        };
      },
    );

    // Filtrar productos que FALTAN por empacar (no están completos)
    const packingProducts = productsWithRealProcessed.filter(
      (product: {
        id: string;
        name: string;
        code: string;
        image: string;
        expectedQuantity: number;
        processedQuantity: number;
      }) => {
        // Si la preparación está completa, no mostrar nada (ya está todo empacado)
        if (data?.preparation?.status === 3) {
          return false;
        }

        // Mostrar productos que NO están completos (faltan por empacar)
        // Incluir productos con 0 procesados y productos parcialmente empacados
        const isIncomplete = product.processedQuantity < product.expectedQuantity;

        return isIncomplete;
      },
    );

    // Calcular estadísticas globales para el header (empaquetados X de Y)
    // Contador superior: incluir asignaciones en curso sumando processed por producto
    const totalProcessed = productsWithRealProcessed.reduce(
      (sum: number, product: { processedQuantity: number }) => sum + Number(product.processedQuantity || 0),
      0,
    );
    const totalExpected = products.reduce(
      (sum: number, product: { expectedQuantity: number }) => sum + product.expectedQuantity,
      0,
    );

    // (sin logs)

    return (
      <ModalCenter
        ref={ref}
        title={t('packing_products_modal.title')}
        type="default"
        width="lg"
        icon={<FontAwesomeIcon icon={faShoppingBag} className="w-4 h-4 text-icons" />}
      >
        <div className="space-y-6">
          {/* Header con progreso general */}
          <div className="flex items-center gap-2 mb-4">
            <div className="text-sm">
              {t('packing_products_modal.packed_summary', {
                packed: totalProcessed,
                total: totalExpected,
              })}
            </div>
          </div>

          {/* Lista de productos en packing */}
          <div className="space-y-0 max-h-96 overflow-y-auto">
            {packingProducts.map(
              (
                product: {
                  id: string;
                  name: string;
                  image: string;
                  code: string;
                  processedQuantity: number;
                  expectedQuantity: number;
                },
                index: number,
              ) => (
                <div
                  key={product.id}
                  className={`flex items-center justify-between py-3 ${
                    index < packingProducts.length - 1 ? 'border-b border-lines' : ''
                  }`}
                >
                  {/* Información del producto */}
                  <div className="flex items-center gap-3 flex-1">
                    <Product id={parseInt(product.id)} name={product.name} src={product.image} code={product.code} />
                  </div>

                  {/* Progreso de cantidad */}
                  <div className="text-xs font-medium text-title bg-card-border rounded-full px-3 py-1">
                    {product.processedQuantity} / {product.expectedQuantity}
                  </div>
                </div>
              ),
            )}
          </div>

          {/* Mensaje cuando no hay productos pendientes */}
          {packingProducts.length === 0 && (
            <div className="text-center py-8">
              <p className="text-complementary-text text-xs">
                {data?.preparation?.status === 3
                  ? t('packing_products_modal.all_products_packed')
                  : t('packing_products_modal.no_products_in_packing')}
              </p>
            </div>
          )}
        </div>
      </ModalCenter>
    );
  },
);

PackingProductsModal.displayName = 'PackingProductsModal';
